import React from "react";

export const COLORS = {
  amber: "#FFC349",
  indigo: "#525EA7",
  sky: "#5FACD3",
  cyan: "#97DDE9",
  ink: "#141733",
  paper: "#FFF8EA",
};

export default function GlobalStyle() {
  return (
    <style>{`
      :root{
        --amber:${COLORS.amber};
        --indigo:${COLORS.indigo};
        --sky:${COLORS.sky};
        --cyan:${COLORS.cyan};
        --ink:${COLORS.ink};
        --paper:${COLORS.paper};
      }
      *{box-sizing:border-box;}
      html{scroll-behavior:smooth;}
      body{margin:0;}
      .bb-root{
        font-family:'Nunito',sans-serif;
        color:var(--ink);
        background:var(--paper);
        overflow-x:hidden;
        background-image:radial-gradient(rgba(20,23,51,0.07) 1.4px, transparent 1.4px);
        background-size:16px 16px;
      }
      h1,h2,h3{font-family:'Bangers',cursive; margin:0; line-height:1; letter-spacing:0.02em;}
      p{line-height:1.6; font-weight:600;}
      a{color:inherit; text-decoration:none;}

      @media (prefers-reduced-motion: reduce){
        *{animation-duration:0.001ms !important; transition-duration:0.001ms !important;}
      }

      /* ---------- reveal ---------- */
      .reveal{opacity:0; transform:translateY(24px); transition:opacity .7s cubic-bezier(.2,.7,.2,1), transform .7s cubic-bezier(.2,.7,.2,1);}
      .reveal.is-visible{opacity:1; transform:translateY(0);}

      /* ---------- comic panel utility ---------- */
      .comic-panel{background:#fff; border:3px solid var(--ink); border-radius:14px; box-shadow:7px 7px 0 var(--ink);}

      /* ---------- starburst badge ---------- */
      .burst{
        display:inline-flex; align-items:center; justify-content:center;
        background:var(--amber);
        clip-path:polygon(50% 0%,61% 20%,82% 10%,80% 33%,100% 40%,84% 55%,95% 75%,72% 73%,68% 95%,50% 80%,32% 95%,28% 73%,5% 75%,16% 55%,0% 40%,20% 33%,18% 10%,39% 20%);
        padding:14px 10px;
        min-width:86px; min-height:86px;
        font-family:'Bangers',cursive;
        font-size:13px; color:var(--ink); text-align:center;
        transform:rotate(-8deg);
      }
      .burst-small{min-width:66px; min-height:66px; font-size:10.5px; padding:8px;}

      /* ---------- nav ---------- */
      .nav{position:fixed; top:0; left:0; right:0; z-index:50; padding:18px 6vw; transition:background .3s, box-shadow .3s, padding .3s;}
      .nav-solid{background:rgba(255,248,234,0.92); backdrop-filter:blur(8px); border-bottom:3px solid var(--ink); padding:12px 6vw;}
      .nav-inner{display:flex; align-items:center; justify-content:space-between; max-width:1180px; margin:0 auto;}
      .brand{font-family:'Bangers',cursive; font-size:24px; display:flex; align-items:center; gap:8px; color:var(--indigo); letter-spacing:0.03em;}
      .brand-mark{color:var(--amber); font-size:14px; -webkit-text-stroke:1px var(--ink);}
      .nav-links{display:flex; align-items:center; gap:26px; font-size:14px; font-weight:700;}
      .nav-links a{opacity:.8; transition:opacity .2s;}
      .nav-links a:hover{opacity:1;}
      .nav-link-active{opacity:1 !important; text-decoration:underline; text-decoration-color:var(--amber); text-decoration-thickness:3px; text-underline-offset:6px;}
      .nav-link-btn{background:transparent; border:none; color:var(--ink); font-family:'Nunito',sans-serif; font-size:14px; font-weight:700; opacity:.8; cursor:pointer; padding:0;}
      .nav-link-btn:hover{opacity:1;}
      .nav-cta{background:var(--amber); color:var(--ink) !important; padding:9px 18px; border-radius:999px; border:2.5px solid var(--ink); opacity:1 !important; box-shadow:4px 4px 0 var(--ink); cursor:pointer; font-family:'Nunito',sans-serif; font-weight:700; font-size:14px;}
      .nav-burger{display:none; flex-direction:column; gap:5px; background:none; border:none; cursor:pointer; padding:6px; z-index:61;}
      .nav-burger span{width:24px; height:3px; background:var(--ink); border-radius:2px; transition:transform .2s;}

      /* ---------- hero ---------- */
      .hero{min-height:100vh; display:grid; grid-template-columns:1.05fr 0.95fr; align-items:center; gap:40px; padding:150px 6vw 90px;}
      .hero > *{min-width:0;}
      .page-hero > *{min-width:0;}
      .eyebrow{display:inline-block; font-family:'Space Mono', monospace; font-size:12px; letter-spacing:.05em; color:var(--ink); background:var(--cyan); padding:7px 14px; border-radius:999px; margin-bottom:20px; border:2.5px solid var(--ink);}
      .hero-title{font-size:clamp(38px,5vw,66px); max-width:660px; -webkit-text-stroke:1.4px var(--ink); text-shadow:3px 3px 0 var(--amber);}
      .hero-sub{font-size:18px; max-width:520px; margin:26px 0 32px; color:rgba(20,23,51,0.8); font-weight:700;}
      .hero-actions{display:flex; gap:16px; flex-wrap:wrap;}
      .btn{display:inline-flex; align-items:center; justify-content:center; padding:14px 24px; border-radius:12px; font-weight:800; font-size:15px; cursor:pointer; transition:transform .15s, box-shadow .15s; border:3px solid var(--ink); font-family:'Nunito',sans-serif;}
      .btn:hover{transform:translate(2px,2px); box-shadow:2px 2px 0 var(--ink) !important;}
      .btn-primary{background:var(--amber); color:var(--ink); box-shadow:6px 6px 0 var(--ink);}
      .btn-ghost{background:#fff; color:var(--indigo); box-shadow:6px 6px 0 var(--ink);}
      .hero-visual{position:relative; min-width:0;}
      .gate-stage{width:100%; aspect-ratio:16/10; min-height:200px; max-height:380px; border:3px solid var(--ink); border-radius:16px; box-shadow:7px 7px 0 var(--ink); background:#fff; overflow:hidden;}

      /* ---------- 3D scene ---------- */
      .scene3d{width:100%; height:100%; position:relative;}
      .scene3d canvas{display:block; border-radius:inherit;}

      /* ---------- sections ---------- */
      .section{padding:110px 6vw;}
      .section-light{background:transparent;}
      .section-indigo{background:var(--indigo); border-top:4px solid var(--ink); border-bottom:4px solid var(--ink);}
      .section-sky{background:var(--sky); border-top:4px solid var(--ink); border-bottom:4px solid var(--ink);}
      .section-cta{background:var(--ink); color:#fff; text-align:center;}
      .section-title{font-size:clamp(30px,3.8vw,48px); max-width:760px;}
      .section-title.on-dark{color:#fff; text-shadow:3px 3px 0 var(--ink);}
      .section-lede{max-width:640px; font-size:17px; margin-top:16px; color:rgba(20,23,51,0.78); font-weight:700;}
      .section-lede.on-dark{color:rgba(255,255,255,0.9);}
      .section-cta .section-title{color:var(--amber); text-shadow:3px 3px 0 rgba(255,255,255,0.15); margin-left:auto; margin-right:auto;}
      .section-cta .section-lede{margin-left:auto; margin-right:auto; color:rgba(255,255,255,0.78);}

      /* ---------- problem ---------- */
      .problem-grid{margin-top:48px; display:flex; flex-direction:column; gap:14px; max-width:820px;}
      .problem-row{display:flex; align-items:center; gap:20px; padding:18px 22px; font-size:16.5px; font-weight:700; background:#fff; border:3px solid var(--ink); border-radius:12px; box-shadow:5px 5px 0 var(--ink);}
      .problem-index{font-family:'Bangers',cursive; color:var(--sky); -webkit-text-stroke:1px var(--ink); font-size:26px; flex-shrink:0;}

      /* ---------- pillars ---------- */
      .pillar-grid{margin-top:52px; display:grid; grid-template-columns:repeat(3,1fr); gap:24px;}
      .tilt-card{will-change:transform; transition:transform .15s ease-out;}
      .pillar-card{background:#fff; border:3px solid var(--ink); border-radius:16px; padding:26px; height:100%; box-shadow:6px 6px 0 var(--ink);}
      .pillar-card h3{font-size:22px; margin:12px 0 10px; color:var(--indigo);}
      .pillar-card p{font-size:14.5px; color:rgba(20,23,51,0.75); margin:0;}
      .pillar-tag{display:inline-block; font-family:'Space Mono', monospace; font-size:11px; letter-spacing:.06em; color:var(--ink); background:var(--amber); padding:4px 10px; border-radius:6px; border:2px solid var(--ink);}

      .demo-row{margin-top:70px; display:grid; grid-template-columns:1fr 360px; gap:40px; align-items:center;}
      .demo-row-reverse{grid-template-columns:360px 1fr; margin-top:50px;}
      .demo-row-reverse .demo-copy{order:2;}
      .demo-row-reverse > div:first-child{order:2;}
      .demo-row-reverse > div:last-child{order:1;}
      .demo-copy h3{color:#fff; font-size:27px; margin:14px 0 12px; text-shadow:2px 2px 0 var(--ink);}
      .demo-copy p{color:rgba(255,255,255,0.85); max-width:420px;}

      .demo-panel{background:#fff; border:3px solid var(--ink); border-radius:18px; width:100%; max-width:340px; margin:0 auto; box-shadow:8px 8px 0 var(--ink); overflow:hidden;}
      .demo-panel-head{display:flex; align-items:center; gap:10px; padding:14px 16px; background:var(--indigo); border-bottom:3px solid var(--ink);}
      .demo-avatar{width:32px; height:32px; border-radius:50%; background:var(--amber); color:var(--ink); display:flex; align-items:center; justify-content:center; font-weight:700; font-family:'Bangers',cursive; border:2px solid var(--ink); flex-shrink:0;}
      .demo-head-text{color:#fff; font-family:'Space Mono', monospace; font-size:12px; letter-spacing:.05em;}
      .demo-body{min-height:200px; padding:18px; display:flex; flex-direction:column; gap:14px;}

      .speech{position:relative; max-width:82%; padding:12px 15px; border:2.5px solid var(--ink); border-radius:16px; font-size:13.5px; font-weight:700; animation:pop .3s ease-out; background:#fff;}
      .speech-me{align-self:flex-end; background:var(--sky);}
      .speech-me::after{content:""; position:absolute; right:14px; bottom:-9px; width:0; height:0; border:9px solid transparent; border-top-color:var(--sky); border-bottom:0;}
      .speech-them{align-self:flex-start; background:var(--cyan);}
      .speech-them::after{content:""; position:absolute; left:14px; bottom:-9px; width:0; height:0; border:9px solid transparent; border-top-color:var(--cyan); border-bottom:0;}
      @keyframes pop{from{opacity:0; transform:translateY(6px) scale(.9);} to{opacity:1; transform:translateY(0) scale(1);}}

      .pay-toast{display:flex; align-items:center; gap:12px; animation:pop .35s ease-out;}
      .pay-toast-text{font-size:13.5px; font-weight:700; background:var(--paper); border:2.5px solid var(--ink); border-radius:12px; padding:10px 13px;}
      .ledger{display:flex; flex-direction:column; gap:8px; animation:pop .35s ease-out;}
      .ledger-row{display:flex; align-items:center; gap:10px; padding:8px 10px; border:2px solid var(--ink); border-radius:10px; font-size:13px; font-weight:700;}
      .ledger-flat{font-family:'Space Mono', monospace; color:var(--indigo); flex-shrink:0;}
      .ledger-name{flex:1; color:rgba(20,23,51,0.75);}
      .stamp{font-family:'Bangers',cursive; font-size:12px; padding:3px 10px; border-radius:6px; border:2px dashed var(--ink); transform:rotate(-4deg); flex-shrink:0;}
      .stamp-paid{background:var(--amber); color:var(--ink);}
      .stamp-pending{background:#fff; color:var(--ink);}

      /* ---------- market ---------- */
      .stat-grid{margin-top:48px; display:grid; grid-template-columns:repeat(4,1fr); gap:22px;}
      .stat-card{background:#fff; border:3px solid var(--ink); border-radius:16px; padding:26px 22px; box-shadow:6px 6px 0 var(--ink);}
      .stat-num{font-family:'Bangers',cursive; font-size:38px; color:var(--indigo);}
      .stat-label{margin-top:10px; font-size:13.5px; color:rgba(20,23,51,0.7); font-weight:700;}
      .market-note{margin-top:36px; max-width:760px; font-size:15px; font-weight:700; padding:20px 22px; background:var(--amber); border:3px solid var(--ink); border-radius:14px; box-shadow:5px 5px 0 var(--ink);}

      /* ---------- gtm ---------- */
      .gtm-grid{margin-top:48px; display:grid; grid-template-columns:repeat(4,1fr); gap:20px;}
      .gtm-card{background:#fff; border-radius:16px; padding:26px; border:3px solid var(--ink); height:100%; box-shadow:6px 6px 0 var(--ink);}
      .gtm-card h3{font-size:19px; margin-bottom:10px; color:var(--indigo);}
      .gtm-card p{font-size:14px; color:rgba(20,23,51,0.72); margin:0;}

      /* ---------- cta ---------- */
      .cta-form-wrap{max-width:520px; margin:44px auto 0;}
      .cta-form{display:flex; flex-direction:column; gap:12px;}
      .cta-form input{padding:14px 16px; border-radius:10px; border:2.5px solid rgba(255,255,255,0.4); background:rgba(255,255,255,0.08); color:#fff; font-size:15px; font-family:'Nunito',sans-serif; font-weight:700;}
      .cta-form input::placeholder{color:rgba(255,255,255,0.5);}
      .cta-form .btn{margin-top:6px;}
      .cta-success{background:var(--amber); color:var(--ink); border:3px solid var(--ink); padding:18px; border-radius:12px; font-size:15px; font-weight:700; box-shadow:5px 5px 0 rgba(255,255,255,0.2);}

      /* ---------- app download ---------- */
      .app-download-section{padding:60px 6vw; background:var(--sky); border-top:4px solid var(--ink);}
      .app-download-container{max-width:1180px; margin:0 auto; background:#fff; border:3px solid var(--ink); border-radius:16px; padding:30px 40px; box-shadow:7px 7px 0 var(--ink); display:flex; align-items:center; justify-content:space-between; gap:20px; flex-wrap:wrap;}
      .app-download-content{display:flex; align-items:center; gap:20px;}
      .app-download-content h2{font-size:32px; color:var(--indigo); margin-bottom:6px;}
      .app-download-content p{margin:0; font-size:15px; color:rgba(20,23,51,0.8);}

      /* ---------- footer ---------- */
      .footer{padding:36px 6vw; background:var(--amber); color:rgba(20,23,51,0.75); border-top:4px solid var(--ink);}
      .footer-top{display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:16px; padding-bottom:22px; border-bottom:2px solid rgba(20,23,51,0.18);}
      .footer .footer-brand{color:var(--ink);}
      .footer-links{display:flex; align-items:center; gap:20px; font-weight:700; font-size:14px; flex-wrap:wrap;}
      .footer-links a{opacity:.75; color:var(--ink);}
      .footer-links a:hover{opacity:1;}
      .footer-link-btn{background:transparent; border:none; color:var(--ink); font-family:'Nunito',sans-serif; font-weight:700; font-size:14px; opacity:.75; cursor:pointer; padding:0;}
      .footer-link-btn:hover{opacity:1;}
      .footer-divider{opacity:0.3; margin:0 4px;}
      .footer-socials{display:flex; gap:14px;}
      .careers-box{max-height:0; opacity:0; overflow:hidden; margin:0; background:#fff; border:3px solid var(--ink); border-radius:12px; box-shadow:5px 5px 0 var(--ink); font-size:14px; color:var(--ink); font-weight:600; max-width:640px; padding:0 18px; transition:max-height .3s ease, opacity .3s ease, margin .3s ease, padding .3s ease;}
      .careers-box.is-open{max-height:200px; opacity:1; margin:18px 0; padding:16px 18px;}
      .careers-box a{color:var(--indigo); text-decoration:underline;}
      .footer-bottom{display:flex; align-items:center; flex-wrap:wrap; gap:10px; padding-top:18px; font-family:'Space Mono', monospace; font-size:12px; color:rgba(20,23,51,0.7);}
      .footer-dot{opacity:0.5;}

      /* ---------- dedicated pages ---------- */
      .page-hero{position:relative; min-height:60vh; display:grid; grid-template-columns:1.1fr 0.9fr; align-items:center; gap:40px; padding:150px 6vw 70px; overflow:hidden;}
      .page-hero-bg{position:absolute; inset:0; opacity:0.5; pointer-events:none; z-index:0;}
      .page-hero-inner{position:relative; z-index:1;}
      .page-hero-scene{position:relative; z-index:1; width:100%; aspect-ratio:16/10; min-height:200px; max-height:380px; border:3px solid var(--ink); border-radius:18px; background:#fff; box-shadow:8px 8px 0 var(--ink); overflow:hidden;}
      .back-link{display:inline-flex; align-items:center; gap:8px; margin-bottom:28px; padding:8px 16px 8px 12px; font-weight:800; font-size:13px; font-family:'Space Mono',monospace; color:var(--indigo); background:#fff; border:2.5px solid var(--ink); border-radius:999px; box-shadow:3px 3px 0 var(--ink); transition:transform .15s, box-shadow .15s;}
      .back-link-arrow{display:inline-block; transition:transform .15s;}
      .back-link:hover{transform:translate(2px,2px); box-shadow:1px 1px 0 var(--ink);}
      .back-link:hover .back-link-arrow{transform:translateX(-3px);}
      .page-title{font-size:clamp(34px,4.6vw,58px); max-width:640px; -webkit-text-stroke:1.2px var(--ink); text-shadow:3px 3px 0 var(--amber);}
      .page-lede{font-size:17px; max-width:520px; margin-top:22px; color:rgba(20,23,51,0.8); font-weight:700;}
      .page-body{padding:0 6vw 100px; position:relative; z-index:1;}
      .page-section{max-width:1180px; margin:0 auto; padding:70px 0; border-top:2px solid rgba(20,23,51,0.12);}
      .page-section:first-child{border-top:none;}
      .section-btn{display:inline-flex; align-items:center; gap:8px; margin-top:28px; font-weight:800; font-size:14px; background:#fff; color:var(--indigo); padding:12px 20px; border-radius:999px; border:2.5px solid var(--ink); box-shadow:4px 4px 0 var(--ink); transition:transform .15s, box-shadow .15s;}
      .section-btn:hover{transform:translate(2px,2px); box-shadow:2px 2px 0 var(--ink);}

      .about-grid{display:grid; grid-template-columns:1fr 1fr; gap:32px; margin-top:16px;}
      .about-card{background:#fff; border:3px solid var(--ink); border-radius:16px; padding:26px; box-shadow:6px 6px 0 var(--ink);}
      .about-card h3{color:var(--indigo); font-size:21px; margin-bottom:10px;}
      .about-card p{font-size:14.5px; color:rgba(20,23,51,0.75); margin:0;}
      .founder-row{display:flex; align-items:center; gap:20px; margin-top:36px; padding:22px; background:var(--amber); border:3px solid var(--ink); border-radius:16px; box-shadow:6px 6px 0 var(--ink);}
      .founder-avatar{width:64px; height:64px; border-radius:50%; background:var(--ink); color:var(--amber); display:flex; align-items:center; justify-content:center; font-family:'Bangers',cursive; font-size:26px; flex-shrink:0; border:3px solid var(--ink);}

      /* ---------- responsive ---------- */
      @media (max-width: 980px){
        .hero{grid-template-columns:1fr; padding-top:130px;}
        .pillar-grid{grid-template-columns:repeat(2,1fr);}
        .demo-row, .demo-row-reverse{grid-template-columns:1fr;}
        .demo-row-reverse > div:first-child, .demo-row-reverse > div:last-child{order:0;}
        .stat-grid{grid-template-columns:repeat(2,1fr);}
        .gtm-grid{grid-template-columns:repeat(2,1fr);}
        .app-download-container{flex-direction:column; text-align:center;}
        .app-download-content{flex-direction:column; text-align:center;}
        .page-hero{grid-template-columns:1fr; padding-top:130px;}
        .about-grid{grid-template-columns:1fr;}
      }
      @media (max-width: 860px){
        .nav-burger{display:flex;}
        .nav-links{
          position:fixed; top:0; right:-100%; height:100vh; width:78vw; max-width:340px;
          background:var(--paper); flex-direction:column; align-items:flex-start; gap:22px;
          padding:100px 28px 40px; box-shadow:-8px 0 0 var(--ink); border-left:4px solid var(--ink);
          transition:right .3s ease; z-index:60;
        }
        .nav-links-open{right:0;}
        .nav-cta{width:100%; text-align:center;}
      }
      @media (max-width: 560px){
        .pillar-grid{grid-template-columns:1fr;}
        .stat-grid{grid-template-columns:1fr;}
        .gtm-grid{grid-template-columns:1fr;}
        .footer-socials{flex-wrap:wrap; justify-content:center;}
        .gate-stage, .page-hero-scene{aspect-ratio:16/9; max-height:260px;}
      }
    `}</style>
  );
}